import React, { useState, useMemo, useEffect, useCallback } from "react";
import { GoogleMap, InfoWindow, Marker, MarkerClusterer, useJsApiLoader } from "@react-google-maps/api";
import NumberFormat from "react-number-format";

// ponytail: the same key the product-page map already uses in public HTML. Move it
// to credentials and restrict it by HTTP referrer when the maps go live on the
// high-traffic results pages, or the billing is anyone's to spend.
const MAPS_API_KEY = "AIzaSyCJ5oNEfyzA-ycLRJkaPWD1EinqoSOh2uM";

// products.lat / products.lon are NOT NULL DEFAULT 0, so 0,0 is the app's "no
// venue" sentinel rather than a point in the Gulf of Guinea. 41% of live
// products sit there on purpose — the operator arranges the venue with the guest.
//
// is_mobile overrules a coordinate rather than standing in for a missing one:
// the activity travels to the guest, so any lat/lon on it is the operator's base
// and pinning it would be wrong rather than merely imprecise.
export const hasPoint = ( product ) =>
	!product.is_mobile && Number( product.lat ) !== 0 && Number( product.lon ) !== 0;

// A single marker fits to a nonsense zoom, so clamp after fitBounds rather than
// forcing one zoom level for every result set.
const MAX_ZOOM = 15;

const CONTAINER_STYLE = { width: "100%", height: "100%" };

// URL-encoded rather than base64: the grid is server-side prerendered
// (ApplicationHelper#grid2 passes prerender: true), and window.btoa at module
// scope would run inside the server renderer, where there is no window.
const CLUSTER_ICON = '<svg xmlns="http://www.w3.org/2000/svg" width="56.499" height="74" viewBox="0 0 56.499 74"><g transform="translate(1623.5 22583.199)"><g transform="translate(-2606 -23325)"><path d="M23.552,68.588C3.687,39.789,0,36.834,0,26.25a26.25,26.25,0,1,1,52.5,0c0,10.584-3.687,13.539-23.552,42.338a3.283,3.283,0,0,1-5.4,0Zm2.7-31.4A10.937,10.937,0,1,0,15.312,26.25,10.937,10.937,0,0,0,26.25,37.187Z" transform="translate(984.5 743.801)" fill="#fd932b"/><path d="M26.25,70a3.258,3.258,0,0,0,2.7-1.412C48.812,39.789,52.5,36.834,52.5,26.25a26.25,26.25,0,1,0-52.5,0C0,36.834,3.687,39.789,23.552,68.588A3.258,3.258,0,0,0,26.25,70m0-54.688A10.937,10.937,0,1,1,15.312,26.25,10.937,10.937,0,0,1,26.25,15.312M26.25,72a5.281,5.281,0,0,1-4.342-2.274c-3.54-5.132-6.52-9.377-9.148-13.122C6.754,48.049,3.129,42.884.955,38.669A24.49,24.49,0,0,1-2,26.25,28.242,28.242,0,0,1,46.225,6.274,28.168,28.168,0,0,1,54.5,26.25a24.49,24.49,0,0,1-2.955,12.419c-2.173,4.215-5.8,9.379-11.8,17.935-2.629,3.745-5.608,7.989-9.146,13.119A5.283,5.283,0,0,1,26.25,72Zm0-54.688a8.937,8.937,0,1,0,8.937,8.938A8.948,8.948,0,0,0,26.25,17.312Z" transform="translate(984.5 743.801)" fill="#fff"/></g><path d="M17,0A17.255,17.255,0,0,1,34,17.5,17.255,17.255,0,0,1,17,35,17.255,17.255,0,0,1,0,17.5,17.255,17.255,0,0,1,17,0Z" transform="translate(-1612 -22572)" fill="#fd932b"/><path d="M17,0A17.255,17.255,0,0,0,0,17.5,17.255,17.255,0,0,0,17,35,17.255,17.255,0,0,0,34,17.5,17.255,17.255,0,0,0,17,0m0-2A18.466,18.466,0,0,1,24.416-.458a18.915,18.915,0,0,1,6.04,4.191,19.47,19.47,0,0,1,4.059,6.2,20.034,20.034,0,0,1,0,15.144,19.47,19.47,0,0,1-4.059,6.2,18.915,18.915,0,0,1-6.04,4.191,18.607,18.607,0,0,1-14.831,0,18.915,18.915,0,0,1-6.04-4.191,19.47,19.47,0,0,1-4.059-6.2,20.034,20.034,0,0,1,0-15.144,19.47,19.47,0,0,1,4.059-6.2A18.915,18.915,0,0,1,9.584-.458,18.466,18.466,0,0,1,17-2Z" transform="translate(-1612 -22572)" fill="#fff"/></g></svg>';

const CLUSTER_OPTIONS = {
	gridSize: 10,
	maxZoom: 20,
	styles: [{
		width: 50,
		height: 50,
		url: 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent( CLUSTER_ICON ),
		textColor: 'white',
		textSize: 20,
		textAlign: 'center',
	}]
};

// The map and the results list are deliberately independent — hovering or clicking
// a pin does nothing to the cards, and vice versa. Use one or the other.
//
// highlighted is the only thing that crosses over, and it isn't a mouse event: the
// ids the concierge measured for a "how far apart are these" answer, so the guest
// can see which pins the answer is about.
const ProductMap = ({ products, cdnPath, path, linkQuery, highlighted }) => {

	const { isLoaded } = useJsApiLoader({
		id: "google-map-script",
		googleMapsApiKey: MAPS_API_KEY
	});

	const [ map, setMap ] = useState( null );
	const [ activeMarker, setActiveMarker ] = useState( null );

	const markers = useMemo(() => products.filter( hasPoint ).map( product => ({
		id:       product.id,
		name:     product.name,
		// The sort cache stores supplier as a string, unlike Product#format_json's
		// { id, name } — reading .name here silently rendered "undefined".
		supplier: product.supplier,
		price:    product.price,
		image:    product.image,
		slug:     product.slug,
		position: { lat: Number( product.lat ), lng: Number( product.lon ) }
	})), [ products ]);

	// Refit whenever the result set changes, not just on first load — filtering or
	// a concierge refinement replaces every marker.
	useEffect(() => {
		if( !map || !markers.length ) return

		const bounds = new window.google.maps.LatLngBounds();
		markers.forEach( marker => bounds.extend( marker.position ) );
		map.fitBounds( bounds );

		window.google.maps.event.addListenerOnce( map, 'bounds_changed', () => {
			if( map.getZoom() > MAX_ZOOM ) map.setZoom( MAX_ZOOM )
		});
	}, [ map, markers ]);

	const onLoad   = useCallback( instance => setMap( instance ), [] );
	const onUnmount = useCallback( () => setMap( null ), [] );

	const isHighlighted = ( id ) => Array.isArray( highlighted ) && highlighted.includes( id )

	if( !isLoaded || !markers.length ) return null

	return (
		<GoogleMap
			mapContainerStyle={CONTAINER_STYLE}
			onLoad={onLoad}
			onUnmount={onUnmount}
			zoom={13}
		>
			<MarkerClusterer options={CLUSTER_OPTIONS}>
				{clusterer =>
					markers.map(({ id, name, supplier, price, slug, image, position }) => (
						<Marker
							key={id}
							position={position}
							clusterer={clusterer}
							onClick={() => setActiveMarker( id )}
							icon={{
								url: isHighlighted( id ) ? "/images/svg/map-pointer-highlight.svg" : "/images/svg/map-pointer.svg",
								scaledSize: new window.google.maps.Size( 60, 60 )
							}}
						>
							{activeMarker === id && (
								<InfoWindow onCloseClick={() => setActiveMarker( null )}>
									{/* The whole popup is the link. There was a View button, but
									    .gm-style-iw-d forces overflow: hidden, so whatever sits at the
									    bottom is silently cut off with no scrollbar — and that was the
									    only way to open the product. The new-tab icon is the same one
									    the cards use, so it reads as "opens the product page". */}
									<a
										className="activity-map__popup"
										href={`${path}/${slug}${linkQuery || ""}`}
										target="_blank"
										rel="noopener"
									>
										<div className="activity-map__popup--image">
											{image && <img src={`${cdnPath}/${image}?w=400&h=300&fit=crop&auto=compress`} alt={name}/>}
											<span className="product-grid__product--new-tab" title="Opens in a new tab">
												<span className="icon icon-external-link med white"></span>
											</span>
										</div>
										<div className="activity-map__popup--name">{name}</div>
										<div className="activity-map__popup--supplier">{supplier}</div>
										<div className="activity-map__popup--price">
											{/* Was a bare {price}, which rendered the raw decimal as "£58.0".
											    Same formatting as the cards. */}
											<p>From <span><NumberFormat
												value={price}
												displayType={"text"}
												thousandSeparator={true}
												prefix={"£"}
												decimalScale={2}
												fixedDecimalScale={true}
											/></span> pp</p>
										</div>
									</a>
								</InfoWindow>
							)}
						</Marker>
					))
				}
			</MarkerClusterer>
		</GoogleMap>
	)
}

export default React.memo( ProductMap );
